// ArcGIS legend display shared by the track map (map-base.js) and the MapLayer admin preview
// (map.mjs): fetches the service's /legend resource (see arcgisLegendUrl for the
// MapServer/FeatureServer-sibling rule), shapes it to the selected sublayers, and renders a
// collapsible bottom-right Leaflet control. Leaflet is reached via window.L (external/global in
// the tracking bundle, bundled in the map bundle). Availability is proven, never assumed: every
// failure path resolves to null and the caller shows nothing.

import { htmlEscape } from "./html-escape.mjs";
import { arcgisUrlShape, parseSublayerIds, arcgisLegendUrl, filterLegendToLayers } from "./arcgis-service.mjs";
import { fetchItemOverrideDrawingInfo } from "./arcgis-item-overrides.mjs";
import { legendItemsFromDrawingInfo } from "./arcgis-renderer-legend.mjs";
import { fetchSublayerMetadata } from "./arcgis-sublayer-metadata.mjs";
import { planEffectiveRender } from "./arcgis-feature-layer.mjs";

// Session cache of raw /legend documents per service base URL. The PROMISE is cached, so
// concurrent probes for one service share a single request — and later resolutions apply in
// attachment order, so the newest caller's rendering wins deterministically. A definitive
// ArcGIS error body caches null (no legend resource — probed once per page); a transient
// failure (network blip, 5xx) evicts its entry so the next toggle retries instead of leaving
// the legend suppressed for the whole session.
const legendDocumentCache = new Map();

function fetchLegendDocument(base) {
	const url = arcgisLegendUrl(base);
	if (url == null) {
		return Promise.resolve(null);
	}
	let promise = legendDocumentCache.get(base);
	if (promise == null) {
		promise = fetch(url + "?f=json", { headers: { Accept: "application/json" } })
			.then((response) => {
				if (!response.ok) {
					throw new Error(`HTTP ${response.status}`);
				}
				return response.json();
			})
			.then((json) => (json != null && typeof json === "object" && json.error == null ? json : null))
			.catch(() => {
				legendDocumentCache.delete(base);
				return null;
			});
		legendDocumentCache.set(base, promise);
	}
	return promise;
}

// Pure: one synthesized legend section (the { layerId, layerName, items } shape
// buildLegendSectionHtml consumes) from a sublayer's metadata + optional ArcGIS Online item
// override, or null when nothing is renderable. Exported for unit tests.
export function buildSynthesizedSection(metadata, overrideDrawingInfo, layerId) {
	if (metadata == null || typeof metadata !== "object") {
		return null;
	}
	// The legend must describe what the map draws, and that is planEffectiveRender's decision —
	// not a straight override-else-service pick. The override is discarded when the service has
	// no drawingInfo (the renderers decorator never swaps it in), and an effective renderer the
	// Arcade shim can't evaluate falls back to esri-leaflet's default; in both cases the map
	// shows plain default symbology, and synthesizing authored swatches would put a legend on
	// screen that contradicts every feature — worse than no legend at all.
	const plan = planEffectiveRender(metadata.drawingInfo, overrideDrawingInfo);
	if (plan.mode === "default") {
		return null;
	}
	const drawingInfo = overrideDrawingInfo != null ? overrideDrawingInfo : metadata.drawingInfo;
	const items = legendItemsFromDrawingInfo(drawingInfo);
	if (items.length === 0) {
		return null;
	}
	return {
		layerId: layerId,
		layerName: typeof metadata.name === "string" ? metadata.name : "",
		items: items
	};
}

// Hosted FeatureServers expose no /legend REST resource, so when the legend document yields no
// sections, synthesize them from each sublayer's effective renderer — the ArcGIS Online
// item-authored override when one exists (the same one the feature build renders with), else the
// service drawingInfo. Every failure drops to a smaller (possibly empty) legend, never an error.
function synthesizeSections(base, ids) {
	return Promise.all(ids.map((id) => fetchSublayerMetadata(base, id).then((metadata) => {
		if (metadata == null) {
			return null;
		}
		return fetchItemOverrideDrawingInfo(metadata.serviceItemId, base, id)
			.then((override) => buildSynthesizedSection(metadata, override, id));
	}))).then((sections) => sections.filter((section) => section != null));
}

// Pure HTML for one layer's legend section. Every dynamic string is escaped; swatches are data:
// URIs straight from the service (the site's img-src already allows data:). With a title, a
// single-sublayer section skips the per-sublayer heading — the layer title above it is enough.
// With a blank title (the add-form preview before a name is typed), the sublayer name is
// promoted as the heading instead, so the section is still identified without duplicating the
// control's own "Layers Legend" header.
export function buildLegendSectionHtml(title, sections) {
	if (sections == null || sections.length === 0) {
		return "";
	}
	const hasTitle = typeof title === "string" && title.trim() !== "";
	let html = `<div class="gsat-legend-section mb-2">`;
	if (hasTitle) {
		html += `<div class="font-weight-bold">${htmlEscape(title)}</div>`;
	}
	for (const section of sections) {
		if ((sections.length > 1 || !hasTitle) && section.layerName !== "") {
			html += `<div class="text-muted small mt-2">${htmlEscape(section.layerName)}</div>`;
		}
		for (const item of section.items) {
			// Finite-integer coercion (not just null checks): the builder is exported, so it must not
			// trust a caller to have sanitized width/height into attribute-safe values.
			const size = (Number.isFinite(item.width) ? ` width="${Math.trunc(item.width)}"` : "")
				+ (Number.isFinite(item.height) ? ` height="${Math.trunc(item.height)}"` : "");
			html += `<div class="gsat-legend-row"><img src="data:${htmlEscape(item.contentType)};base64,${htmlEscape(item.imageData)}"${size} alt="" /> <span>${htmlEscape(item.label)}</span></div>`;
		}
	}
	return html + "</div>";
}

// Resolves a stored layer (root or legacy sublayer URL + CSV) to its legend HTML, or null when
// no legend is available for the effective sublayers.
export function loadArcgisLegendHtml(title, url, layersCsv) {
	const shape = arcgisUrlShape(typeof url === "string" ? url : "");
	const ids = shape.sublayerId !== null ? [Number(shape.sublayerId)] : parseSublayerIds(layersCsv);
	if (shape.base === "" || ids.length === 0) {
		return Promise.resolve(null);
	}
	return fetchLegendDocument(shape.base).then((document) => {
		const sections = filterLegendToLayers(document, ids);
		if (sections.length > 0) {
			const html = buildLegendSectionHtml(title, sections);
			return html === "" ? null : html;
		}
		// No server legend (hosted FeatureServer, or a MapServer with its legend disabled):
		// synthesize from the effective renderers instead.
		return synthesizeSections(shape.base, ids).then((synthesized) => {
			if (synthesized.length === 0) {
				return null;
			}
			const html = buildLegendSectionHtml(title, synthesized);
			return html === "" ? null : html;
		});
	});
}

// --- Per-map collapsible control (bottom-right). State keyed by the Leaflet map instance so the
// admin preview and the track map can never share/clobber each other's control. Header/toggle
// mimic the track map's other collapsible panels (#map-mode-minimize): a btn-secondary chevron
// button flipping between #angle-up (expanded) and #angle-down (collapsed) in the shared
// tracking.svg sprite, with the title switching between the expanded and collapsed strings. ---
const controlStates = new WeakMap(); // map -> { control, body, toggle, title, sections: Map<key, element> }

const CHEVRON_EXPANDED = "/content/svg/tracking.svg?v=15#angle-up";
const CHEVRON_COLLAPSED = "/content/svg/tracking.svg?v=15#angle-down";

// Collapsed/expanded survives reloads as a localStorage preference, same convention as the other
// map-UI state (see apl:refreshIntervalMs in map-adsb-feed-dialog.js): namespaced key,
// best-effort try/catch access (private mode / quota / disabled), absent = expanded.
const COLLAPSED_STORAGE_KEY = "arcgisLegend:collapsed";

function readStoredCollapsed() {
	try {
		return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "1";
	} catch (error) {
		return false;
	}
}

function storeCollapsed(collapsed) {
	try {
		window.localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? "1" : "0");
	} catch (error) {
		// Best-effort persistence only.
	}
}

// Localized control titles arrive as the setLegendSection titles parameter — the same
// caller-supplies-strings pattern as the popup labels (registerIdentifyLayer,
// buildFeaturePropertiesPopup). No window global, no untranslated fallback: a missing
// parameter shows an empty header, visibly, instead of silently shipping English.
function ensureControl(map, titles) {
	let state = controlStates.get(map);
	if (state != null) {
		if (titles != null) {
			state.titles = titles;
		}
		return state;
	}
	const L = window.L;
	const control = L.control({ position: "bottomright" });
	state = {
		control: control,
		body: null,
		toggle: null,
		title: null,
		sections: new Map(),
		titles: titles != null ? titles : { title: "", titleCollapsed: "" }
	};
	control.onAdd = function () {
		const container = L.DomUtil.create("div", "gsat-arcgis-legend");
		// The header IS the toggle button: full-width, title span filling the row with the chevron
		// pinned at the end. Plain .btn (no variant class) — the dark toolbar colors come from the
		// gsat-arcgis-legend-header rules, which mimic the map toolbar's dark .btn-outline-dark.
		const toggle = L.DomUtil.create("button", "gsat-arcgis-legend-header btn d-flex align-items-center text-left", container);
		toggle.type = "button";
		// Static markup only — no dynamic strings reach this innerHTML.
		toggle.innerHTML = `<span class="gsat-arcgis-legend-title w-100 mr-2"></span><svg><use xlink:href="${CHEVRON_EXPANDED}"></use></svg>`;
		const title = toggle.querySelector("span");
		const body = L.DomUtil.create("div", "gsat-arcgis-legend-body", container);
		const applyCollapsed = (collapse) => {
			body.style.display = collapse ? "none" : "";
			title.textContent = collapse ? state.titles.titleCollapsed : state.titles.title;
			const use = toggle.querySelector("use");
			if (use != null) {
				use.setAttributeNS("http://www.w3.org/1999/xlink", "href", collapse ? CHEVRON_COLLAPSED : CHEVRON_EXPANDED);
			}
		};
		L.DomEvent.on(toggle, "click", function () {
			const collapse = body.style.display !== "none";
			applyCollapsed(collapse);
			storeCollapsed(collapse);
		});
		L.DomEvent.disableClickPropagation(container);
		L.DomEvent.disableScrollPropagation(container);
		// The stored preference decides the initial state (a control re-created later in the
		// session — e.g. the last layer toggled off and on — honors it too, not just page load).
		applyCollapsed(readStoredCollapsed());
		state.body = body;
		state.toggle = toggle;
		state.title = title;
		return container;
	};
	control.addTo(map);
	controlStates.set(map, state);
	return state;
}

// Deterministic section order: keys compare by prefix then numeric suffix ("custom-9" before
// "custom-12"), so the legend lists layers in stable id order no matter which /legend fetch
// resolves first, and a toggled-off/on layer returns to its slot instead of dropping to the
// bottom. Pure; exported for unit tests.
export function legendInsertBeforeKey(existingKeys, newKey) {
	const parse = (key) => {
		const match = /^(.*?)(\d+)$/.exec(key);
		return match == null
			? { prefix: key, number: null }
			: { prefix: match[1], number: Number(match[2]) };
	};
	const compare = (a, b) => {
		const left = parse(a);
		const right = parse(b);
		if (left.prefix !== right.prefix || left.number == null || right.number == null) {
			return a < b
				? -1
				: a > b
					? 1
					: 0;
		}
		return left.number - right.number;
	};
	let before = null;
	for (const existingKey of existingKeys) {
		if (compare(newKey, existingKey) < 0 && (before == null || compare(existingKey, before) < 0)) {
			before = existingKey;
		}
	}
	return before;
}

// Adds/replaces one layer's legend section on the map's control (created lazily), inserted at
// its stable key-ordered position (see legendInsertBeforeKey). titles = { title,
// titleCollapsed } localized header strings; the last caller's titles win (each surface passes
// one constant pair per page).
export function setLegendSection(map, key, html, titles) {
	const state = ensureControl(map, titles);
	let element = state.sections.get(key);
	if (element == null) {
		element = document.createElement("div");
		const beforeKey = legendInsertBeforeKey([...state.sections.keys()], key);
		state.sections.set(key, element);
		state.body.insertBefore(element, beforeKey == null ? null : state.sections.get(beforeKey));
	}
	element.innerHTML = html;
}

// Removes one layer's section; the control itself is removed with its last section.
export function removeLegendSection(map, key) {
	const state = controlStates.get(map);
	if (state == null) {
		return;
	}
	const element = state.sections.get(key);
	if (element != null) {
		state.sections.delete(key);
		if (element.parentNode != null) {
			element.parentNode.removeChild(element);
		}
	}
	if (state.sections.size === 0) {
		state.control.remove();
		controlStates.delete(map);
	}
}
